"use client";

import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import React, { useEffect, useRef } from "react";

/** Desktop background is a bundled asset (not admin-driven), matching the reference. */
const DESKTOP_BACKGROUND = "/images/banner-bg.webp";

/** Half of the 432px hero image — the dark band covers the hero's right half. */
const HERO_HALF = 216;

/**
 * Translucent scrim laid over an uploaded mobile background so the white
 * headline stays readable on light or busy photos. Only applied when an image
 * is actually set — the bundled fallback art is already dark enough.
 */
const MOBILE_SCRIM = "rgba(3, 7, 18, 0.05)";

export interface Banner1010Data {
  id?: string;
  status?: boolean;
  /** Headline — `{ }` / `{{ }}` markers highlight words */
  title?: string;
  title_color?: string;
  title_highlight_color?: string;
  /** Right-panel hero image (432×432). The desktop background is bundled. */
  image?: string;
  /** Hero image alt text */
  image_alt?: string;
  /** Mobile background (falls back to the bundled desktop art) */
  mobile_image?: string;
  /** Dark right panel / side bleed colour */
  background_color?: string;
  hero_section_height?: number | string;
  hero_container_max_width?: number | string;
}

interface Banner1010Props {
  data: Banner1010Data | { data: Banner1010Data };
}

function resolvePayload(
  data: Banner1010Props["data"],
): Banner1010Data | undefined {
  const anyData = data as { data?: Banner1010Data };
  if (anyData?.data && typeof anyData.data === "object") {
    return anyData.data as Banner1010Data;
  }
  return data as Banner1010Data;
}

/**
 * Colours come straight from the CMS, so they are written as inline styles
 * rather than arbitrary Tailwind classes (fresh arbitrary classes are not in
 * the compiled stylesheet). Strip anything that could escape the attribute.
 */
function safeColor(raw: string | undefined, fallback: string): string {
  const value = (raw ?? "").trim();
  if (!value) return fallback;
  return /^[#a-zA-Z0-9(),.%\s-]+$/.test(value) ? value : fallback;
}

/**
 * Highlight `{ }` (inline) / `{{ }}` (inline + line break) words in the title.
 * Mirrors `formatSectionTitle`, but emits an inline colour instead of a class
 * so the admin-picked highlight colour renders without a Tailwind rebuild.
 */
function buildTitleHtml(title: string, highlightColor: string): string {
  if (!title) return "";
  const open = `<span style="color:${highlightColor}">`;

  if (title.includes("{{")) {
    return title
      .replace(/\{\{([^}]+)\}\}/g, `${open}$1</span><br/>`)
      .replace(/\s+/g, " ");
  }

  if (title.includes("{")) {
    return title.replace(/\{/g, open).replace(/\}/g, "</span>").replace(/\s+/g, " ");
  }

  return title;
}

/**
 * Banner 1010 — Banner 9's hero layout without the carousel or any of its
 * per-slide extras (no pre-title, subtitle, programme pills, CTAs, EMI strip
 * or accreditation logos). A full-bleed background carries a single headline,
 * vertically centred in the left 3/5; on desktop a dark band bleeds from the
 * 3:2 seam to the viewport edge and holds a 432×432 hero image straddling it.
 *
 * Below 1024px the right panel is hidden and the headline centres over the
 * uploaded mobile background. The headline is fixed white there (the admin
 * colour is for the light desktop layout and would be unreadable on the
 * photo); the `{ }` highlight keeps its CMS colour at every breakpoint.
 */
const Banner1010 = ({ data }: Banner1010Props) => {
  const sectionData = resolvePayload(data);
  const ref = useRef<HTMLElement>(null);

  const {
    status = true,
    title = "",
    title_color = "#FFFFFF",
    title_highlight_color = "#FFD700",
    image = "",
    image_alt = "banner",
    mobile_image = "",
    background_color = "rgba(3, 7, 18, 1)",
    hero_section_height = 564,
    hero_container_max_width = 1114,
  } = sectionData || {};

  useEffect(() => {
    if (ref.current && status) {
      FireWidgetViewedEvent(ref, "banner_1010");
    }
  }, [status]);

  if (!status) return null;

  const accentStripColor = safeColor(background_color, "rgba(3, 7, 18, 1)");
  const titleColor = safeColor(title_color, "#FFFFFF");
  const highlightColor = safeColor(title_highlight_color, "#FFD700");

  const sectionHeight = Number(hero_section_height) || 564;
  const contentMaxPx = Number(hero_container_max_width) || 1114;
  const rightGutterHalfPx = contentMaxPx / 2;

  const heroImage = String(image ?? "").trim();
  const mobileBg = String(mobile_image ?? "").trim();
  const hasHero = Boolean(heroImage);

  const titleHtml = buildTitleHtml(title, highlightColor);

  return (
    <section
      ref={ref}
      data-banner="1010"
      className="relative isolate w-full overflow-hidden"
      style={{ height: sectionHeight, fontFamily: "Poppins, sans-serif" }}
    >
      {/* Full-bleed background. Desktop is the bundled asset; mobile uses the
          admin upload when present and falls back to the same asset. */}
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <Image
          src={mobileBg || DESKTOP_BACKGROUND}
          alt={image_alt || "banner-bg"}
          fill
          priority
          fetchPriority="high"
          sizes="100vw"
          className="object-cover object-center min-[1024px]:hidden"
        />
        <Image
          src={DESKTOP_BACKGROUND}
          alt={image_alt || "banner-bg"}
          fill
          priority
          fetchPriority="high"
          sizes="100vw"
          className="hidden object-cover object-center min-[1024px]:block"
        />
        {/* Mobile-only scrim over the uploaded photo (desktop keeps the bundled
            art untinted, and its headline uses the admin colour). */}
        {mobileBg ? (
          <div
            className="absolute inset-0 min-[1024px]:hidden"
            style={{ backgroundColor: MOBILE_SCRIM }}
            aria-hidden
          />
        ) : null}
      </div>

      {/* Static right band: the gutter beyond the content container, plus —
          when a hero image is set — the sliver between the hero's right edge
          (80% + 216px of the container) and the viewport edge. Each term
          clamps to 0 separately, so a container narrower than 1080px
          (0.2 × width < 216px) clips the band at the container edge. */}
      <div
        className="absolute top-0 right-0 z-[1] hidden h-full min-[1024px]:block"
        style={{
          width: hasHero
            ? `calc(max(50% - min(50%, ${rightGutterHalfPx}px), 0px) + max(min(20%, ${contentMaxPx / 5}px) - ${HERO_HALF}px, 0px))`
            : `calc(50% - ${rightGutterHalfPx}px)`,
          minWidth: 0,
          backgroundColor: accentStripColor,
        }}
        aria-hidden
      />

      <div
        className="mx-auto h-full overflow-hidden"
        style={{ width: "100%", maxWidth: contentMaxPx }}
      >
        <div className="flex h-full w-full flex-col min-[1024px]:flex-row">
          {/* Headline column — vertically centred, since nothing sits below it. */}
          <div className="flex h-full w-full min-[1024px]:flex-[3]">
            {/* No desktop width cap and no extra left gutter: the headline
                starts at the container's px-5 edge and uses the whole 3/5
                column (~628px of text width at a 1114px container), so long
                titles break as late as possible. The px-5 right padding still
                keeps ~27px of clearance from the hero image's left edge. */}
            {/* Mobile runs to a 4px gutter rather than px-5 so the headline
                spans the same full width as the background image behind it. At
                36px that 32px of reclaimed width is what gets "Placement
                Support?" (376px) onto one line on a 390px phone instead of
                splitting it. Desktop keeps px-5 — there the right padding is
                the ~27px of clearance from the hero image's left edge. */}
            <div className="flex h-full w-full flex-col items-center justify-center px-1 py-5 min-[1024px]:items-start min-[1024px]:px-5 min-[1024px]:py-10">
              {titleHtml ? (
                <h1
                  className={cn(
                    // 36px at every breakpoint, matching the reference (whose
                    // inline fontSize overrode its own text-[24px] class).
                    "z-10 text-center text-[36px] font-bold leading-[52px] min-[1024px]:text-left",
                    // `pretty`, not `balance`: balance evens every line, which
                    // pulls words off the first one. `pretty` keeps the greedy
                    // fill — line 1 runs as long as the column allows — and only
                    // reflows the tail so the last line isn't a lone orphan
                    // (…Campus Immersion and 8th / Convocation). Purely
                    // presentational: browsers without it fall back to the plain
                    // greedy wrap, which already gives the same long first line.
                    "text-pretty",
                    // Mobile sits on the uploaded background photo, so the
                    // headline is fixed white for contrast; the admin colour
                    // takes over from 1024px up. The `{ }` highlight carries
                    // its own inline colour on the span, so it wins over both
                    // and stays the CMS colour at every breakpoint.
                    "text-[#FFFFFF] min-[1024px]:text-[color:var(--b1010-title)]",
                  )}
                  style={
                    { ["--b1010-title"]: titleColor } as React.CSSProperties
                  }
                  dangerouslySetInnerHTML={{ __html: titleHtml }}
                />
              ) : null}
            </div>
          </div>

          {/* Right panel: dark band with the 432×432 hero image (desktop only). */}
          {hasHero ? (
            <div className="relative hidden h-full min-[1024px]:block min-[1024px]:flex-[2]">
              <div
                className="absolute inset-y-0 left-1/2"
                style={{
                  width: `${HERO_HALF}px`,
                  backgroundColor: accentStripColor,
                }}
              >
                <div className="absolute flex h-full w-[432px] -translate-x-1/2 items-center justify-center">
                  {/* No object-fit — the image fills the full 432×432 box, as
                      in the reference. Banner 9 uses object-contain here, which
                      letterboxes non-square uploads and reads as a smaller
                      image. */}
                  <Image
                    src={heroImage}
                    alt={image_alt || "banner"}
                    width={432}
                    height={432}
                    priority
                    fetchPriority="high"
                    className="h-[432px] w-[432px]"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default Banner1010;
